
import React, { useState } from 'react'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { AppBar, Toolbar, Typography, MenuProps } from '@mui/material'
import DropdownMenu from 'smartstream-widgets/controls/DropdownMenu/DropdownMenu'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { OddsFormat } from './../types'
import { PropTranslations } from 'smartstream-widgets/types/widget'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import DynamicImage from 'smartstream-widgets/controls/DynamicImage/DynamicImage'

type Props = {
  classes: {
    [K in string]: string
  },
  config: {
    BASE_ASSET_PATH: string,
    oddsFormat: OddsFormat,
    topBarCompetitionLogo: boolean
  },
  competitionId: string,
  competitionName: string,
  oddFormatHandler: ((arg0: string) => void),
  theme: Theme,
  translations: PropTranslations,
  mobileView: boolean
}

type Option = {
  id: string,
  label: string | number
}

const styles = (theme: Theme) =>  createStyles({
  appBar: {
    justifyContent: 'center',
    background: theme.palette.primary.main,
    minHeight: 35
  },
  competitionLogo: {
    height: 19
  },
  containerLogo: {
    width: 'initial'
  },
  logo: {
    height: 20,
    verticalAlign: 'middle'
  },
  dropDown: {
    fontSize: 9,
    fontWeight: 'bold',
    margin: theme.spacing(1),
    minWidth: 98,
    color: 'inherit'
  },
  dropDownInput: {
    lineHeight: 1,
    textTransform: 'uppercase',
    '&::before':{
      content: (props: Props) => (`'${props.translations.odds}: '`),
    },
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  dropDownArrow: {
    minWidth: 'initial',
    marginLeft: 'auto',
    '& > div': {
      margin: 0,
    },
    '& svg': {
      color: 'inherit'
    }
  },
  poweredBy: {
    fontSize: 11,
    marginRight: 8,
    fontFamily: 'Klarheit Kurrent'
  },
  title: {
    fontFamily: `${theme.typography.variantFontFamily}, ${theme.typography.fontFamily}`,
    fontSize: 21,
    fontWeight: 'bold',
    marginRight: 21
  },
  toolBar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    minHeight: 'auto'
  },
  toolBarMobile: {
    height: 40,
    '& $poweredBy': {
      fontSize: 6,
      textTransform: 'uppercase',
      fontWeight: 'bold',
      margin: 0,
      '& span' : {
        lineHeight: .8
      }
    },
    '& $logo': {
      margin: 0,
      height: 24
    }
  },
  selectEmpty: {
    marginTop: theme.spacing(2)
  },
  itemSelected: {
    color: theme.palette.primary.main
  },
  oddsFormatList: {
    padding: 0,
    borderRadius: 0
  },
  oddsFormatPaper: {
    borderRadius: 0
  },
  oddsFormatMobilePaper: {
    borderRadius: 0,
    width: '100%'
  }
})

export function getDropDownItems(translations: PropTranslations): Array<Option> {
  const oddsFormats = ['american', 'fraction', 'decimal']
  return oddsFormats.map(oddsFormat => {
    return {
      label: translations[oddsFormat],
      id: oddsFormat
    }
  })
}

function getPopOverProps (mobileView, classes: { [K in string]: string }): Partial<MenuProps> {
  return mobileView ?
    {
      anchorReference: 'anchorPosition',
      anchorPosition: {
        left: 0,
        top: 0
      },
      marginThreshold: 10,
      classes: {
        list: classes.oddsFormatList,
        paper: classes.oddsFormatMobilePaper
      }
    }
    :
    {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      classes: {
        list: classes.oddsFormatList,
        paper: classes.oddsFormatPaper
      }
    }
}

export function TopBar (props: Props) {
  const {
    classes,
    config: {
      BASE_ASSET_PATH,
      topBarCompetitionLogo = false,
      oddsFormat = 'american'
    },
    competitionId,
    competitionName,
    oddFormatHandler,
    theme,
    translations,
    mobileView
  } = props

  const menuItems = getDropDownItems(translations)

  const [selectedFormat, setSelectedFormat] = useState(oddsFormat)

  const onChange = (value: OddsFormat) => {
    setSelectedFormat(value)
    oddFormatHandler && oddFormatHandler(value)
  }

  const logoVersion = theme.palette.mode ||'light'
  const logoUrl = `${BASE_ASSET_PATH}/img/genius-sports-logos/genius-sports-media-horizontal-${logoVersion}.svg`
  const barClass = mobileView ? `${classes.toolBar} ${classes.toolBarMobile}` : classes.toolBar
  const popOverProps = getPopOverProps(mobileView, classes)

  return (
    <AppBar position='static' className={classes.appBar} data-testid='top-bar'>
      <Toolbar className={barClass}>
        {
          !mobileView && (topBarCompetitionLogo || competitionName) &&
          <Typography component='h1' className={classes.title}>
            {
              topBarCompetitionLogo ?
              <DynamicImage supportedFormats={['webp']}>
                <img
                  className={classes.competitionLogo}
                  src={`${BASE_ASSET_PATH}/img/competition-logos/${competitionId}-${theme.palette.mode}.svg`}
                />
              </DynamicImage>
              : competitionName
            }
            {` ${translations.odds}`}
          </Typography>
        }
        <Typography className={classes.poweredBy}>
          <span>{translations.poweredBy}</span>
          <picture>
            <img
              className={classes.logo}
              src={logoUrl}
              alt='Powered by'
            />
          </picture>
        </Typography>
        {competitionName &&
        <DropdownMenu
          InputProps={{
            classes: { root: classes.dropDown, input: classes.dropDownInput },
            disableUnderline: true
          }}
          className={classes.dropDownArrow}
          classes={classes}
          options={menuItems}
          selectedOptionId={selectedFormat}
          onValueChange={onChange}
          SelectProps={{
            MenuProps: popOverProps
          }}
        />
        }
      </Toolbar>
    </AppBar>
  )
}

export default compose(
  withComponentContext,
  withTranslations((t: TranslationFunction) => {
    return {
      american: t('american.translation', {
        defaultValue: 'American',
        context: 'menu item for american odd format inside Odds Comparison Grid widget'
      }),
      decimal: t('decimal.translation', {
        defaultValue: 'Decimal',
        context: 'menu item for decimal odd format inside Odds Comparison Grid widget'
      }),
      fraction: t('fraction.translation', {
        defaultValue: 'Fraction',
        context: 'menu item for fraction odd format inside Odds Comparison Grid widget'
      }),
      odds: t('odds.translation', {
        defaultValue: 'odds',
        context: 'display the odds competition tittle inside Odds Comparison Grid widget'
      }),
      poweredBy: t('poweredBy.translation', {
        defaultValue: 'Powered by',
        context: 'Text to indicate authoring, before the Genius Sports logo, inside Odds Comparison Grid widget'
      })
    }
  }),
  withStyles(styles, { name: 'TopBar', withTheme: true, defaultTheme: getDefaultTheme })
)(TopBar)
